<template>
  <div class="contact-links">
    <a v-if="emailLink" :href="`mailto:${emailLink}`" class="contact-btn">
      <Icon name="email" :size="20" />
      {{ primaryText }}
    </a>
    <a
      v-for="social in socialLinks"
      :key="social.icon"
      :href="social.link"
      target="_blank"
      rel="noopener"
      class="contact-btn contact-btn--secondary"
    >
      <Icon :name="social.icon" :size="20" />
      {{ iconLabels[social.icon] || social.icon }}
    </a>
  </div>
</template>

<script setup lang="ts">
import { useData } from "vitepress";
import { computed } from "vue";
import { Icon } from "../../primitives/icons";

interface Props {
  primaryText?: string;
}

withDefaults(defineProps<Props>(), {
  primaryText: "Get in Touch",
});

const { theme } = useData();
const allSocialLinks = theme.value.socialLinks || [];

// Extract email from social links (look for mailto: link)
const emailLink = computed(() => {
  const found = allSocialLinks.find((link: { link: string }) =>
    link.link?.startsWith("mailto:"),
  );
  return found?.link?.replace("mailto:", "") || null;
});

// Filter out email from displayed social links
const socialLinks = computed(() =>
  allSocialLinks.filter(
    (link: { link: string }) => !link.link?.startsWith("mailto:"),
  ),
);

const iconLabels: Record<string, string> = {
  github: "GitHub",
  linkedin: "LinkedIn",
};
</script>

<style scoped lang="scss">
.contact-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-md);
  margin-top: var(--space-xl);
}

.contact-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-2xl);
  background-color: var(--color-accent);
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition:
    background-color 0.2s ease,
    transform 0.2s ease;
  color: white;

  &:hover,
  &:focus {
    background-color: var(--color-accent-dark);
    transform: translateY(-2px);
    color: unset;
    text-decoration: underline;
  }

  &:active {
    transform: translateY(0);
  }

  &--secondary {
    background-color: transparent;
    color: var(--color-text-base);
    border: 2px solid var(--color-border);

    &:hover,
    &:focus {
      background-color: var(--color-background-soft);
      border-color: currentColor;
      text-decoration: underline;
    }
  }
}
</style>
